import { useQuery } from '@apollo/client'
import {
  createContext,
  FC,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react'

import { Alert } from '@mui/material'
import TagManager from 'react-gtm-module'
import OneSignal from 'react-onesignal'
import { Account, User } from 'src/@types/graphql'
import ResCenterMigrationBanner from 'src/components/ResCenterMigrationBanner/ResCenterMigrationBanner'
import { env, isRunningLocally } from 'src/helpers'
import { identifySentry } from 'src/logger/sentry'
import { GET_ALL_PERMISSION, GET_AUTHED_USER } from './data/queries'
import { getPermissions } from './helpers/transformdata'
import { useAuth } from './AuthProvider'

const SUPER_ADMIN = 'Super Admin'

const UserContext = createContext<{
  account?: Account
  user?: User
  loading: boolean
  canEditAccount: boolean
  canEditAccountBillingDetails: boolean
  canEditAccountContactDetails: boolean
  canEditUser: boolean
  canViewRoles: boolean
  canCreateAdmins: boolean
  canViewAdmins: boolean
  canEditAdmins: boolean
  canDeleteAdmins: boolean
  canViewContacts: boolean
  canViewPhoneSettings: boolean
  canViewSettings: boolean
  canViewTags: boolean
  canViewProperties: boolean
  canCreateTag: boolean
  canCreateContacts: boolean
  canCreateAutomations: boolean
  canCreateNotes: boolean
  canCreateRoles: boolean
  canEditRoles: boolean
  canCreateExtensions: boolean
  canEditGeneralSettings: boolean
  canEditTag: boolean
  canCreateProperty: boolean
  canEditProperty: boolean
  canEditPropertyPhone: boolean
  canEditContact: boolean
  canEditExtensions: boolean
  canDisableTags: boolean
  canDeleteExtensions: boolean
  canDeleteAutomations: boolean
  canEditAutomations: boolean
  canViewAutomations: boolean
  canManageIntegrations: boolean
  canManagePaymentPlans: boolean
  viewCommunicationCenter: boolean
  viewLeasingInbox: boolean
  viewRescomInbox: boolean
  viewOtherInbox: boolean
  viewArchivedInbox: boolean
  sendEmail: boolean
  sendText: boolean
  makeVoiceCall: boolean
  receiveVoiceCall: boolean
  canListenToVoiceRecordings: boolean
  assignConversation: boolean
  claimConversation: boolean
  moveConversation: boolean
  workSpaceSettingsGroupPermission: boolean
  communicationCenterGroupPermission: boolean
  automationEngineGroupPermission: boolean
  accountabilityGroupPermission: boolean
  contactsGroupPermission: boolean
  propertiesGroupPermission: boolean
  isPermReady: boolean
  isSuperAdmin: () => boolean
  canViewAllTasks: boolean
  canCreateTasks: boolean
  canEditTasks: boolean
  checkPermission: (permissionId) => void
  PERMISSIONS: any
  canViewGlobalAccounts: boolean
  canViewGlobalConversation: boolean
  showSystemAccess: boolean
  hasBuzzRegionalManagerRole: boolean
  hasAccountOwnerRole: boolean
  payReadyUser?: {
    id: number
    pay_ready_user_id: number
    recovery_access: boolean
    eviction_access: boolean
    resident_access: boolean
    user_type?: string | null
  } | null
  intercomData?: {
    app_id: string
    api_base: string
    intercom_user_jwt: string | null
    email: string | null
    user_id: string | null
    user_is_pay_ready: boolean | null
    user_products: string | null
    user_properties: string | null
    user_type: string | null
    user_roles: string | null
    name: string | null
    created_at: number | null
    company: {
      id: string | null
      name: string | null
      remote_created_at: number | null
      size: number | null
    } | null
  } | null
}>({
  account: undefined,
  user: undefined,
  loading: false,
  canEditAccount: false,
  canEditAccountBillingDetails: false,
  canEditAccountContactDetails: false,
  canEditUser: false,
  canViewRoles: false,
  canCreateAdmins: false,
  canViewAdmins: false,
  canEditAdmins: false,
  canDeleteAdmins: false,
  canViewContacts: false,
  canViewPhoneSettings: false,
  canViewSettings: false,
  canViewTags: false,
  canViewProperties: false,
  canCreateTag: false,
  canCreateContacts: false,
  canCreateNotes: false,
  canCreateRoles: false,
  canEditRoles: false,
  canCreateExtensions: false,
  canCreateAutomations: false,
  canEditGeneralSettings: false,
  canEditTag: false,
  canCreateProperty: false,
  canEditProperty: false,
  canEditPropertyPhone: false,
  canEditContact: false,
  canEditExtensions: false,
  canDisableTags: false,
  canDeleteAutomations: false,
  canDeleteExtensions: false,
  canEditAutomations: false,
  canViewAutomations: false,
  canManageIntegrations: false,
  canManagePaymentPlans: false,
  viewCommunicationCenter: false,
  viewLeasingInbox: false,
  viewRescomInbox: false,
  viewOtherInbox: false,
  viewArchivedInbox: false,
  sendEmail: false,
  sendText: false,
  makeVoiceCall: false,
  receiveVoiceCall: false,
  assignConversation: false,
  claimConversation: false,
  moveConversation: false,
  canListenToVoiceRecordings: false,
  workSpaceSettingsGroupPermission: false,
  communicationCenterGroupPermission: false,
  automationEngineGroupPermission: false,
  accountabilityGroupPermission: false,
  contactsGroupPermission: false,
  propertiesGroupPermission: false,
  isPermReady: false,
  isSuperAdmin: () => false,
  canViewAllTasks: false,
  canCreateTasks: false,
  canEditTasks: false,
  canViewGlobalAccounts: false,
  canViewGlobalConversation: false,
  checkPermission: () => {},
  PERMISSIONS: [],
  showSystemAccess: false,
  hasBuzzRegionalManagerRole: false,
  hasAccountOwnerRole: false,
  payReadyUser: undefined,
  intercomData: undefined,
})

enum SystemPermission {
  ACCOUNTS = 'ACCOUNTS',
  CONVERSATIONS = 'CONVERSATIONS',
}

enum ROLES {
  BUZZ_REGIONAL_MANAGER = 'Buzz Regional Manager',
  ACOUNT_OWNER = 'Account Owner',
}

export const useUser = () => useContext(UserContext)

export const UserProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const { isAuthenticated } = useAuth()

  //Do not load user data/permissions until authenticated
  const { data, loading } = useQuery<{ authed_user: User }>(GET_AUTHED_USER, {
    skip: !isAuthenticated,
  })
  const { data: all_permissions_data, loading: loading_permissions_data } =
    useQuery(GET_ALL_PERMISSION, {
      skip: !isAuthenticated,
    })

  const user = data?.authed_user
  const email = user?.email
  const account = user?.user_account?.[0]?.account
  const payReadyUser = user?.pay_ready_user?.[0] || null

  const setupOneSignal = useCallback(() => {
    OneSignal.Slidedown.promptPush()
    if (user?.notification_enabled) {
      OneSignal.User.addAlias('externalId', user?.id?.toString())
    } else {
      OneSignal.User.removeAlias('externalId')
    }
  }, [user?.id, user?.notification_enabled])

  useEffect(() => {
    //We do not load tags for local development
    if (!isRunningLocally() && !loading && user) {
      const tagManagerArgs = {
        gtmId: `GTM-5PF52KK`,
        preview: env(`env`),
        dataLayer: {
          userId: user.id,
          userProject: `web`,
          email: user.email,
          fname: user.first_name,
          lname: user.last_name,
          notifications_enabled: user.notification_enabled,
          profile_image: user.profile_image_url,
          ...(user.user_account &&
            !!user.user_account.length && {
              account: user.user_account[0].account?.id,
              account_name: user.user_account[0].account?.name,
            }),
        },
      }
      TagManager.initialize(tagManagerArgs)
      setupOneSignal()
      identifySentry(user.id, user.email, user.first_name, user.last_name)
    }
  }, [data, loading, setupOneSignal, user])

  const {
    PERMISSIONS,
    PERMISSION_GROUPS,
  }: { PERMISSIONS: any; PERMISSION_GROUPS: any } =
    getPermissions(all_permissions_data)

  const checkPermissionInPermissionGroup = useCallback(
    (permissionId: number) =>
      user?.user_role.some((userRole) =>
        userRole?.role?.role_permission_group?.some((rolePermGroup) =>
          rolePermGroup.permission_group.permission?.some(
            (permission) => permission?.id === permissionId,
          ),
        ),
      ),
    [user?.user_role],
  )
  const checkPermissionGroup = useCallback(
    (permissionGroupId: number) =>
      !!user?.user_role.some((userRole) =>
        userRole?.role?.role_permission_group?.some(
          (rolePermGroup) =>
            rolePermGroup.permission_group.id === permissionGroupId,
        ),
      ),
    [user?.user_role],
  )

  const checkSystemPermission = useCallback(
    (permissionName: string) => {
      const isInSystemRolePermission = !!user?.user_system_role.some(
        (userSystemRole) =>
          userSystemRole?.system_role?.system_role_permission?.some(
            (system_role_permission) =>
              system_role_permission.system_permission?.name?.toUpperCase() ===
              permissionName,
          ),
      )
      if (!isInSystemRolePermission) {
        return !!user?.user_system_role.some((userSystemRole) =>
          userSystemRole?.system_role?.system_role_permission_group?.some(
            (system_role_permission_group) =>
              system_role_permission_group.system_permission_group?.system_permission.some(
                (system_permission) =>
                  system_permission.name?.toUpperCase() === permissionName,
              ),
          ),
        )
      }
      return true
    },
    [user?.user_system_role],
  )

  const canViewGlobalAccounts = checkSystemPermission(SystemPermission.ACCOUNTS)
  const canViewGlobalConversation = checkSystemPermission(
    SystemPermission.CONVERSATIONS,
  )

  const showSystemAccess = useMemo(
    () => canViewGlobalConversation || canViewGlobalAccounts,
    [canViewGlobalAccounts, canViewGlobalConversation],
  )

  const isSuperAdmin = useCallback(() => {
    if (showSystemAccess) return true
    return !!user?.user_role.some(
      (userRole: any) => userRole?.role?.name === SUPER_ADMIN,
    )
  }, [showSystemAccess, user?.user_role])

  const checkRoleByName = useCallback(
    (roleName: string) => {
      if (isSuperAdmin()) return true
      // check role
      return !!user?.user_role.some(
        (userRole) => userRole?.role.name === roleName,
      )
    },
    [isSuperAdmin, user?.user_role],
  )

  const checkPermission = useCallback(
    (permissionId: number) => {
      if (isSuperAdmin()) return true
      // check role permission group
      if (checkPermissionInPermissionGroup(permissionId)) return true
      // check role permission
      return !!user?.user_role.some((userRole: any) =>
        userRole?.role?.role_permission?.some(
          (rolePerm: any) => rolePerm?.permission?.id === permissionId,
        ),
      )
    },
    [isSuperAdmin, checkPermissionInPermissionGroup, user?.user_role],
  )

  const hasBuzzRegionalManagerRole = checkRoleByName(
    ROLES.BUZZ_REGIONAL_MANAGER,
  )

  const hasAccountOwnerRole = checkRoleByName(ROLES.ACOUNT_OWNER)

  const canEditGeneralSettings = checkPermission(
    PERMISSIONS.EDIT_WORKSPACE_GENERAL_SETTINGS,
  )
  const canCreateRoles = checkPermission(PERMISSIONS.CREATE_ROLES)
  const canViewRoles = checkPermission(PERMISSIONS.VIEW_ROLES_AND_PERMISSIONS)
  const canEditRoles = checkPermission(PERMISSIONS.EDIT_ROLES)
  const canCreateAdmins = checkPermission(PERMISSIONS.CREATE_ADMINS)
  const canViewAdmins = checkPermission(PERMISSIONS.VIEW_ADMINS)
  const canEditAdmins = checkPermission(PERMISSIONS.EDIT_ADMINS)
  const canDeleteAdmins = checkPermission(PERMISSIONS.DELETE_ADMINS)
  const canViewPhoneSettings = checkPermission(PERMISSIONS.VIEW_EXTENSIONS)
  const canViewSettings = checkPermission(PERMISSIONS.VIEW_INTEGRATIONS)
  const viewCommunicationCenter = checkPermission(
    PERMISSIONS?.VIEW_COMMUNICATION_CENTER,
  )
  const canViewContacts = checkPermission(PERMISSIONS.CONTACTS)
  const canCreateContacts = checkPermission(PERMISSIONS.CREATE_CONTACT)
  const canEditContact = checkPermission(PERMISSIONS.EDIT_CONTACTS)
  const canViewProperties = checkPermission(PERMISSIONS.PROPERTIES)
  const canCreateProperty = checkPermission(PERMISSIONS.CREATE_PROPERTY)
  const canEditProperty = checkPermission(PERMISSIONS.EDIT_PROPERTY)
  const canEditPropertyPhone = checkPermission(PERMISSIONS.EDIT_PROPERTY_PHONE)
  const viewLeasingInbox = checkPermission(PERMISSIONS.VIEW_LEASING_INBOX)
  const viewRescomInbox = checkPermission(PERMISSIONS.VIEW_RESCOM_INBOX)
  const viewOtherInbox = checkPermission(PERMISSIONS.VIEW_OTHER_INBOX)
  const viewArchivedInbox = checkPermission(PERMISSIONS.VIEW_ARCHIVED)
  const sendEmail = checkPermission(PERMISSIONS.SEND_EMAIL)
  const sendText = checkPermission(PERMISSIONS.SEND_TEXT)
  const makeVoiceCall = checkPermission(PERMISSIONS.MAKE_VOICE_CALL)
  const receiveVoiceCall = checkPermission(PERMISSIONS.RECEIVE_VOICE_CALLS)
  const assignConversation = checkPermission(PERMISSIONS.ASSIGN_CONVERSATIONS)
  const claimConversation = checkPermission(PERMISSIONS.CLAIM_CONVERSATIONS)
  const moveConversation = checkPermission(
    PERMISSIONS.MANAGE_CONVERSATION_INBOXES,
  )
  const canViewTags = checkPermission(PERMISSIONS.VIEW_TAGS)
  const canCreateTag = checkPermission(PERMISSIONS.CREATE_TAGS)
  const canEditTag = checkPermission(PERMISSIONS.EDIT_TAGS)
  const canDisableTags = checkPermission(PERMISSIONS.DELETE_TAGS)

  const canCreateNotes = checkPermission(PERMISSIONS.CREATE_NOTES)

  const canCreateExtensions = checkPermission(PERMISSIONS.CREATE_EXTENSIONS)
  const canEditExtensions = checkPermission(PERMISSIONS.EDIT_EXTENSIONS)
  const canDeleteExtensions = checkPermission(PERMISSIONS.DELETE_EXTENSIONS)

  const canCreateAutomations = checkPermission(PERMISSIONS.CREATE_AUTOMATIONS)
  const canDeleteAutomations = checkPermission(PERMISSIONS.DELETE_AUTOMATIONS)
  const canEditAutomations = checkPermission(PERMISSIONS.EDIT_AUTOMATIONS)
  const canViewAutomations = checkPermission(PERMISSIONS.VIEW_AUTOMATION_ENGINE)
  const canManageIntegrations = showSystemAccess //checkPermission(PERMISSIONS.MANAGE_INTEGRATIONS) // disabling
  const canViewAllTasks = checkPermission(PERMISSIONS.VIEW_ALL_TASKS)
  const canCreateTasks = checkPermission(PERMISSIONS.CREATE_TASKS)
  const canEditTasks = checkPermission(PERMISSIONS.EDIT_TASKS)

  const canEditAccount = checkPermission(PERMISSIONS.EDIT_ACCOUNT)
  const canEditAccountBillingDetails = checkPermission(
    PERMISSIONS.EDIT_ACCOUNT_BILLING_DETAILS,
  )
  const canEditAccountContactDetails = checkPermission(
    PERMISSIONS.EDIT_ACCOUNT_CONTACT_DETAILS,
  )

  const canEditUser = checkPermission(PERMISSIONS.EDIT_USER)

  const canManagePaymentPlans = checkPermission(
    PERMISSIONS.MANAGE_PAYMENT_PLANS,
  )
  const canListenToVoiceRecordings =
    checkPermission(PERMISSIONS.LISTEN_TO_RECORDINGS) ||
    hasBuzzRegionalManagerRole

  const workSpaceSettingsGroupPermission = checkPermissionGroup(
    PERMISSION_GROUPS.WORKSPACE_SETTINGS,
  )
  const communicationCenterGroupPermission = checkPermissionGroup(
    PERMISSION_GROUPS.COMMUNICATION_CENTER,
  )
  const automationEngineGroupPermission = checkPermissionGroup(
    PERMISSION_GROUPS.AUTOMATION_ENGINE,
  )
  const accountabilityGroupPermission = checkPermissionGroup(
    PERMISSION_GROUPS.ACCOUNTABILITY,
  )
  const contactsGroupPermission = checkPermissionGroup(
    PERMISSION_GROUPS.CONTACTS,
  )
  const propertiesGroupPermission = checkPermissionGroup(
    PERMISSION_GROUPS.PROPERTIES,
  )

  const isPermReady =
    !!user && all_permissions_data && !loading_permissions_data && !loading

  const intercomData = useMemo(() => {
    if (!user) return undefined

    const isProd = env('cognito_client') !== '1v2m1ihhl2a3dbdhpue48c66uj'
    const userCreatedAt = user.created_at
      ? Math.floor(new Date(user.created_at).getTime() / 1000)
      : undefined
    const companyCreatedAt = account?.created_at
      ? Math.floor(new Date(account?.created_at).getTime() / 1000)
      : undefined
    const account_has_pmo = account?.enable_post_moveout_product || false
    const user_has_properties =
      user.user_property && user.user_property.length > 0
    const properties_have_pmo =
      user.user_property?.some((up) => up.property?.enable_post_moveout) ||
      false
    const pmo_access =
      account_has_pmo && (!user_has_properties || properties_have_pmo)
    const recovery_access = payReadyUser?.recovery_access || false
    const eviction_access = payReadyUser?.eviction_access || false
    const resident_access = payReadyUser?.resident_access || false
    const products = [
      'Current Resident',
      ...(pmo_access ? ['PMO'] : []),
      ...(recovery_access ? ['Pay Ready Recovery'] : []),
      ...(eviction_access ? ['Eviction Center'] : []),
      ...(resident_access ? ['Resident Center'] : []),
    ].join(', ')
    const properties = user.user_property
      ?.map((up) => up.property?.name)
      .join(', ')
      ?.substring(0, 255)
    const roles = user.user_role
      ?.map((ur) => ur.role?.name)
      .join(', ')
      ?.substring(0, 255)
    const user_type = isSuperAdmin()
      ? 'Super Admin'
      : hasBuzzRegionalManagerRole
        ? 'Regional Manager'
        : hasAccountOwnerRole
          ? 'Account Owner'
          : payReadyUser
            ? `PayReady User, ${payReadyUser.user_type || 'Buzz User'}`
            : 'Buzz User'

    return {
      app_id: env('intercom_app_id') || '',
      api_base: 'https://api-iam.intercom.io',
      intercom_user_jwt: user.intercom_user_hash || null,
      email: user.email || null,
      user_id: user.id?.toString() || null,
      user_is_pay_ready: !!payReadyUser,
      user_products: products || null,
      user_properties: properties || null,
      user_roles: roles || null,
      user_type: user_type,
      name: `${user.first_name} ${user.last_name}`.trim() || null,
      created_at: userCreatedAt || null,
      company: {
        id: account?.id?.toString() || null,
        name: account?.name || null,
        remote_created_at: companyCreatedAt || null,
        size: account?.total_units || 0,
      },
    }
  }, [
    user,
    account,
    payReadyUser,
    isSuperAdmin,
    hasBuzzRegionalManagerRole,
    hasAccountOwnerRole,
  ])

  const values = useMemo(
    () => ({
      account,
      user,
      loading,
      intercomData,
      isPermReady,
      isSuperAdmin,
      canEditAccount,
      canEditAccountBillingDetails,
      canEditAccountContactDetails,
      canEditUser,
      canViewRoles,
      canViewAdmins,
      canEditAdmins,
      canDeleteAdmins,
      canViewContacts,
      canViewPhoneSettings,
      canViewSettings,
      canViewTags,
      canViewProperties,
      canCreateAdmins,
      canCreateTag,
      canCreateContacts,
      canCreateNotes,
      canCreateRoles,
      canCreateExtensions,
      canCreateAutomations,
      canEditTag,
      canCreateProperty,
      canEditProperty,
      canEditPropertyPhone,
      canEditContact,
      canEditRoles,
      canEditExtensions,
      canEditGeneralSettings,
      canDisableTags,
      canDeleteAutomations,
      canDeleteExtensions,
      canEditAutomations,
      canViewAutomations,
      canManageIntegrations,
      canViewAllTasks,
      canCreateTasks,
      canEditTasks,
      canManagePaymentPlans,
      viewCommunicationCenter,
      viewLeasingInbox,
      viewRescomInbox,
      viewOtherInbox,
      viewArchivedInbox,
      sendEmail,
      sendText,
      makeVoiceCall,
      receiveVoiceCall,
      assignConversation,
      claimConversation,
      moveConversation,
      canListenToVoiceRecordings,
      workSpaceSettingsGroupPermission,
      communicationCenterGroupPermission,
      automationEngineGroupPermission,
      accountabilityGroupPermission,
      contactsGroupPermission,
      propertiesGroupPermission,
      canViewGlobalAccounts,
      canViewGlobalConversation,
      showSystemAccess,
      hasBuzzRegionalManagerRole,
      hasAccountOwnerRole,
      PERMISSIONS,
      checkPermission,
      payReadyUser,
    }),
    [
      account,
      user,
      loading,
      intercomData,
      isPermReady,
      isSuperAdmin,
      canEditAccount,
      canEditAccountBillingDetails,
      canEditAccountContactDetails,
      canEditUser,
      canViewRoles,
      canViewAdmins,
      canEditAdmins,
      canDeleteAdmins,
      canViewContacts,
      canViewPhoneSettings,
      canViewSettings,
      canViewTags,
      canViewProperties,
      canCreateAdmins,
      canCreateTag,
      canCreateContacts,
      canCreateNotes,
      canCreateRoles,
      canCreateExtensions,
      canCreateAutomations,
      canEditTag,
      canCreateProperty,
      canEditProperty,
      canEditPropertyPhone,
      canEditContact,
      canEditRoles,
      canEditExtensions,
      canEditGeneralSettings,
      canDisableTags,
      canDeleteAutomations,
      canDeleteExtensions,
      canEditAutomations,
      canViewAutomations,
      canManageIntegrations,
      canViewAllTasks,
      canCreateTasks,
      canEditTasks,
      canManagePaymentPlans,
      viewCommunicationCenter,
      viewLeasingInbox,
      viewRescomInbox,
      viewOtherInbox,
      viewArchivedInbox,
      sendEmail,
      sendText,
      makeVoiceCall,
      receiveVoiceCall,
      assignConversation,
      claimConversation,
      moveConversation,
      canListenToVoiceRecordings,
      workSpaceSettingsGroupPermission,
      communicationCenterGroupPermission,
      automationEngineGroupPermission,
      accountabilityGroupPermission,
      contactsGroupPermission,
      propertiesGroupPermission,
      canViewGlobalAccounts,
      canViewGlobalConversation,
      showSystemAccess,
      hasBuzzRegionalManagerRole,
      hasAccountOwnerRole,
      PERMISSIONS,
      checkPermission,
      payReadyUser,
    ],
  )

  //WE only care about locally, and dev, there is no staging and on production we do not present this information
  //FE is based on the hostname user is currently connected to
  const getFrontendEnvironment = () => {
    //Check local first as it is an exeption
    if (isRunningLocally()) {
      return 'local'
    }
    //Then check dev/test for dev/test environments (the rest is production and we don't show alert)
    const hostname = window.location.hostname
    if (hostname.includes('dev.') || hostname.includes('buzz-test.')) {
      return hostname.split('.')[0]
    }

    return '' // We will not show the frontend environment on production
  }

  //We only care if FE is not '' and what we are using as backend.
  //BE is based on the api endpoint the FE is currently connected to
  const getBackendEnvironment = () => {
    const apiEndpoint = env('graphql_api_endpoint')

    //buzz-test-api.payready.com.local:5500 is the only exception
    if (apiEndpoint.includes('local')) {
      return 'local'
    }

    //Return the  first part of the api endpoint before the first dot/:
    return apiEndpoint.split('/')[2].split('.')[0].split(':')[0]
  }

  //Only if we have a value for frontendEnv, we will show the alert, but we need it for the alert color
  const frontendEnv = getFrontendEnvironment()
  const backendEnv = getBackendEnvironment()

  const getAlertSeverity = (env: string) => {
    switch (env) {
      case 'local':
        return 'success' //Green for LOCAL
      case 'staging':
      case 'buzz-test-api':
        return 'warning' //Yellow for TEST/Staging
      case 'api':
      case 'buzz-api':
        return 'error' //Red for PROD
      default:
        return 'info' //Blue for DEV
    }
  }

  return (
    <UserContext.Provider value={values}>
      <>
        {frontendEnv !== '' && (
          <Alert
            variant="filled"
            severity={getAlertSeverity(backendEnv)}
            style={{ borderRadius: 0 }}
          >
            You are viewing the site with the <strong>{frontendEnv}</strong>{' '}
            frontend → <strong>{backendEnv}</strong> backend. You are logged in
            as <strong>{email}</strong> and{' '}
            {showSystemAccess ? (
              <>
                have the <strong>System Admin</strong> role
              </>
            ) : (
              <>
                part of Account #<strong>{account?.id}</strong>({account?.name})
              </>
            )}
          </Alert>
        )}
        <ResCenterMigrationBanner />
        {children}
      </>
    </UserContext.Provider>
  )
}
